import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, WordPuzzleDisplay, playSound, SharedSoundEffect } from '@mental-mint/shared-ui';
import { WordDriftPuzzle } from '../../WordDriftUtils';
import * as Lucide from 'lucide-react';
import { LevelScore } from '@mental-mint/shared-types';

interface LevelSelectionButtonProps {
  puzzle: WordDriftPuzzle;
  levelScore?: LevelScore<any> | null;
  onClick?: () => void;
}

/**
 * Reusable button component for displaying a Word Drift puzzle level.
 * Shows the start/goal words with optional completion status as an overlay badge.
 * Color-coded: green for completed, red for attempted but failed, neutral for unattempted.
 */
export const LevelSelectionButton: React.FC<LevelSelectionButtonProps> = ({
  puzzle,
  levelScore,
  onClick
}) => {
  const { currentGame: game } = useSelector((state: RootState) => state.gameSession);
  const [isHovered, setIsHovered] = useState(false);
  
  if (!game) return null;
  
  // Determine status for color-coding and badge
  const isCompleted = levelScore?.isWon === true;
  const isFailed = levelScore && !levelScore.isWon;
  
  // Subtle color scheme matching Word Drift's sepia aesthetic
  const getBorderColor = () => {
    if (isCompleted) return '#6B8E6B'; // Muted sage green
    if (isFailed) return '#B87E5E'; // Warm terracotta
    return game.colorPrimary || '#8B4513'; // Default sepia
  };
  
  const getBackgroundColor = () => {
    if (isHovered) {
      if (isCompleted) return 'rgba(107, 142, 107, 0.08)';
      if (isFailed) return 'rgba(184, 126, 94, 0.08)';
      return 'rgba(139, 69, 19, 0.05)';
    }
    if (isCompleted) return 'rgba(107, 142, 107, 0.04)';
    if (isFailed) return 'rgba(184, 126, 94, 0.04)';
    return 'rgba(255, 255, 255, 0.95)';
  };
  
  const getBadgeColor = () => {
    if (isCompleted) return '#6B8E6B';
    if (isFailed) return '#B87E5E';
    return 'transparent';
  };
  
  return (
    <div 
      onClick={() => {
        if (onClick) {
          playSound(SharedSoundEffect.MENU_CLICK);
          onClick();
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ 
        cursor: onClick ? 'pointer' : 'default',
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        position: 'relative',
        transition: 'transform 0.15s ease-out',
        transform: isHovered && onClick ? 'translateY(-2px)' : 'translateY(0)'
      }}
      role="button"
      tabIndex={onClick ? 0 : -1}
      aria-label={`${puzzle.startWord} to ${puzzle.goalWord} puzzle${isCompleted ? ' - completed' : isFailed ? ' - attempted' : ' - not attempted'}`}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          playSound(SharedSoundEffect.MENU_CLICK);
          onClick();
        }
      }}
    >
      {/* Status badge overlay - top right corner */}
      {levelScore && (
        <div 
          style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: getBadgeColor(),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            transition: 'transform 0.2s ease-out',
            transform: isHovered ? 'scale(1.1)' : 'scale(1)'
          }}
          aria-hidden="true"
        >
          {isCompleted ? (
            <Lucide.Check size={20} color="white" strokeWidth={3} />
          ) : (
            <Lucide.X size={20} color="white" strokeWidth={3} />
          )}
        </div>
      )}
      
      <WordPuzzleDisplay
        startWord={puzzle.startWord}
        goalWord={puzzle.goalWord}
        primaryColor={game.colorPrimary || '#8B4513'}
        fontFamily={game.fontSecondary}
        showUnknownPath={true}
        unknownPathLength={3}
        capitalize={true}
        containerStyles={{
          border: `2px solid ${getBorderColor()}`,
          backgroundColor: getBackgroundColor(),
          boxShadow: isHovered 
            ? '0 4px 12px rgba(0, 0, 0, 0.15)'
            : '0 2px 6px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-out',
          outline: isHovered && onClick ? `2px solid ${getBorderColor()}` : 'none',
          outlineOffset: '2px'
        }}
      />
    </div>
  );
};

export default LevelSelectionButton;
